/**
 * Calls of the guards module: the guards, their documents and the zone accesses they are
 * assigned to. The roster and the zone accesses are listed with the simple search of the
 * advanced search service.
 *
 * A guard keeps an employee record for its contract, so it is created and updated through the
 * employee service, and its selfie and fingerprints are uploaded as the ones of an employee.
 */
import {createAsyncThunk} from "@reduxjs/toolkit";
import {apiInstance} from "../../Apis/Axios";
import {EMPLOYEE_SERVICE, USER_SERVICE, ZONE_ACCESS_SERVICE} from "../EndPoints";

const multipartConfig = {
    headers: {
        'content-type': 'multipart/form-data',
    },
};

/**
 * Runs the request and answers with its data and status, also when it fails.
 */
const call = async (request) => {
    const result = await request
        .then(function (response) {
            return response;
        })
        .catch(function (error) {
            return error.response;
        });
    const {data, status} = result;
    return {data, status};
};

export const CreateGuardUser = createAsyncThunk(
    'guardsService/createGuardUser',
    async (body) => call(apiInstance.post(EMPLOYEE_SERVICE.createGuard, body)),
);

export const UpdateGuardUser = createAsyncThunk(
    'guardsService/updateGuardUser',
    async (body) => call(apiInstance.put(EMPLOYEE_SERVICE.updateGuard, body)),
);

export const GetGuardDocuments = createAsyncThunk(
    'guardsService/getGuardDocuments',
    async (userId) => call(apiInstance.get(`${USER_SERVICE.userDocumentsByUserId}${userId}`)),
);

/**
 * Uploads a document of a guard with the name it is organized with.
 */
export const AddGuardDocument = createAsyncThunk(
    'guardsService/addGuardDocument',
    async ({userId, name, file}) => {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('file', file);
        return call(apiInstance.post(`${USER_SERVICE.userDocumentsByUserId}${userId}`, formData, multipartConfig));
    },
);

export const DeleteGuardDocument = createAsyncThunk(
    'guardsService/deleteGuardDocument',
    async (id) => call(apiInstance.delete(`${USER_SERVICE.deleteUserDocument}${id}`)),
);

/**
 * Reads the zone accesses to choose one from.
 */
export const GetZoneAccessList = createAsyncThunk(
    'zoneAccessService/getZoneAccessList',
    async () => call(apiInstance.get(ZONE_ACCESS_SERVICE.getAll)),
);

export const GetZoneAccessById = createAsyncThunk(
    'zoneAccessService/getZoneAccessById',
    async (id) => call(apiInstance.get(`${ZONE_ACCESS_SERVICE.getById}${id}`)),
);

/**
 * Reads the zone accesses a guard is assigned to, the data is empty when it has none.
 */
export const GetZoneAccessByGuard = createAsyncThunk(
    'zoneAccessService/getZoneAccessByGuard',
    async (userId) => call(apiInstance.get(`${ZONE_ACCESS_SERVICE.getByGuard}${userId}`)),
);

export const CreateZoneAccess = createAsyncThunk(
    'zoneAccessService/createZoneAccess',
    async (body) => call(apiInstance.post(ZONE_ACCESS_SERVICE.create, body)),
);

export const UpdateZoneAccess = createAsyncThunk(
    'zoneAccessService/updateZoneAccess',
    async ({id, body}) => call(apiInstance.put(`${ZONE_ACCESS_SERVICE.update}${id}`, body)),
);

/**
 * Assigns a guard to the zone accesses sent, or leaves it unassigned when none is sent.
 */
export const AssignGuardZoneAccess = createAsyncThunk(
    'zoneAccessService/assignGuardZoneAccess',
    async (body) => call(apiInstance.put(ZONE_ACCESS_SERVICE.assignGuard, body)),
);

